import { getMethodologyPath } from 'services';
import { leadAnalystParser, documentsParser } from 'shared/parsers';

export const artifactsParser = (data) =>
  data.map(
    ({
      publicationDate,
      shortTitle,
      shortText,
      artifactType,
      restricted,
      relatedArtifacts,
      id,
    }) => ({
      publicationDate,
      title: shortTitle,
      teaser: shortText,
      type: artifactType,
      restricted,
      relatedArtifacts,
      id,
    }),
  );

const ratingsParser = (ratings) =>
  ratings.map(
    ({
      rating,
      preliminaryFlag,
      notchSuffix,
      outlook,
      name,
      leadAnalyst,
      committeeChair,
      demandType,
      ratingAction,
      ratingClassification,
      ratingDate,
      endorsementLocations,
      issuanceLocation,
    }) => ({
      rating,
      preliminaryFlag,
      notchSuffix,
      outlook,
      name,
      leadAnalyst: `${leadAnalyst.firstName} ${leadAnalyst.name}`,
      committeeChair,
      demandType,
      ratingAction,
      ratingClassification,
      ratingDate,
      endorsementLocations,
      issuanceLocation,
    }),
  );

const superLevelParser = ({ approved, available, chargeable, purchased, ratings }) => ({
  approved,
  available,
  chargeable,
  purchased,
  ratings: ratingsParser(ratings),
});

export const productsParser = ({ data }) => {
  const { coi, l1, l2, l3, l4, l5, leadAnalyst } = data;

  return {
    conflictOfInterests: coi,
    l1,
    l2,
    l3,
    l4: superLevelParser(l4),
    l5: superLevelParser(l5),
    ...(leadAnalyst && { leadAnalyst: leadAnalystParser(leadAnalyst) }),
  };
};

const methodologiesParser = (data) =>
  data.map(({ id, fileName, publicationDate, title }) => ({
    path: getMethodologyPath(id),
    name: fileName,
    publicationDate,
    title,
  }));

export const entityParser = ({
  entityApiBasic,
  entityApiData,
  documentsApiData,
  productsApiData,
}) => {
  if (entityApiBasic) {
    const { name, classification, countryCode } = entityApiBasic.data;

    return {
      name,
      industry: classification.name,
      countryCode,
      products: {
        l1: { available: false, ratings: [] },
        l2: { available: false, ratings: [] },
        l3: { available: false, ratings: [] },
        l4: { available: false, ratings: [] },
        l5: { available: false, ratings: [] },
      },
    };
  }

  const {
    name,
    methodologies,
    classification,
    countryCode,
    ultimateIssuer,
    issueCount,
    ratingNewsCount,
    researchCount,
    spv,
  } = entityApiData.data;

  return {
    name,
    methodologies: methodologiesParser(methodologies),
    industry: classification && classification.name,
    industryCode: classification && classification.code,
    countryCode,
    ultimateIssuer,
    issueCount,
    ratingNewsCount,
    researchCount,
    spv,
    products: productsParser(productsApiData),
    ...(documentsApiData && { documents: documentsParser(documentsApiData.data) }),
  };
};

export const instrumentParser = ({ instrumentApiData, documentsApiData, productsApiData }) => {
  const {
    name,
    issuer: entity,
    bond,
    methodologies,
    classification,
    currentAmountOutstandingVolume,
    currentAmountOutstandingDate,
    ratingNewsCount,
    researchCount,
  } = instrumentApiData.data;
  const {
    couponPercent,
    currency,
    isin,
    issueDate,
    maturity,
    maturityPerpetualFlag,
    seniorityType,
    volume,
  } = bond;

  return {
    name,
    entityId: entity.id,
    entityName: entity.name,
    couponPercent,
    currency,
    isin,
    date: issueDate,
    maturity,
    maturityPerpetualFlag,
    ratingNewsCount,
    researchCount,
    seniorityType,
    volume,
    outstandingVolume: currentAmountOutstandingVolume,
    outstandingDate: currentAmountOutstandingDate,
    methodologies: methodologiesParser(methodologies),
    industry: classification && classification.name,
    industryCode: classification && classification.code,
    documents: documentsParser(documentsApiData.data),
    products: productsParser(productsApiData),
  };
};
